/**
 * The one way the web app talks to the backend (/api/v1).
 *
 * - Sends the session cookie, the CSRF token (Django's `csrftoken` cookie, echoed in
 *   X-CSRFToken) and this device's id (X-Device-Id): sessions are bound to the device
 *   they were opened on (PRD section 12).
 * - Turns every failure into an ApiError carrying the server's `{error: {code, message}}`,
 *   whose message is written for members and can be shown as it is.
 * - A request refused because the session has ended (idle 15 minutes, another device)
 *   raises SESSION_ENDED_EVENT so the app can ask for the PIN again.
 * - Polls and quiet refreshes pass `background: true` (X-Background: 1) so they do not
 *   count as use: the server still ends a session after 15 minutes without a person.
 */

import { BACKGROUND_AFTER_MS, idleFor } from '../session/activity'

const BASE = '/api/v1'
const DEVICE_KEY = 'tisunge.device'
export const SESSION_ENDED_EVENT = 'tisunge:session-ended'
/** The server needs her consent first (PRD 12): the app re-reads who she is and shows the join step. */
export const CONSENT_REQUIRED_EVENT = 'tisunge:consent-required'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: Record<string, unknown>
  readonly fields: Record<string, string[]> | undefined

  constructor(status: number, code: string, message: string, details: Record<string, unknown> = {},
    fields?: Record<string, string[]>) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
    this.fields = fields
  }

  /** True when the server no longer accepts this session and the person must sign in again. */
  get sessionEnded(): boolean {
    return (this.status === 401 || this.status === 403) &&
      ['not_authenticated', 'authentication_failed', 'device_unknown'].includes(this.code)
  }
}

/** The server's message for one field of an `invalid` error, if it named that field. */
export function fieldError(error: unknown, field: string): string | undefined {
  if (!(error instanceof ApiError) || !error.fields) return undefined
  const messages = error.fields[field]
  return Array.isArray(messages) && messages.length ? messages.join(' ') : undefined
}

/** A random id for this browser, kept so the server can bind sessions to it. */
export function deviceId(): string {
  try {
    const existing = window.localStorage.getItem(DEVICE_KEY)
    if (existing) return existing
    const fresh = crypto.randomUUID()
    window.localStorage.setItem(DEVICE_KEY, fresh)
    return fresh
  } catch {
    return 'no-storage-device'
  }
}

/** A fresh idempotency key: one per tap of Pay, so a retry after a failure is a new payment intent. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

function csrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/)
  return match?.[1] ? decodeURIComponent(match[1]) : ''
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | undefined | null>
  signal?: AbortSignal
  /** A poll or quiet refresh rather than something she did; it does not keep the session open. */
  background?: boolean
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  // A path built from a link the person followed must stay where it was meant to go (web review finding 7).
  if (path.split('?')[0]?.split('/').some((segment) => segment === '..' || segment === '.')) {
    throw new ApiError(404, 'not_found', 'That page does not exist.')
  }
  const method = options.method ?? 'GET'
  const url = new URL(BASE + path, window.location.origin)
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value))
  }
  const headers: Record<string, string> = { Accept: 'application/json', 'X-Device-Id': deviceId() }
  // A request a minute after her last tap or key is a poll: it must not keep the session open (PRD 12).
  if (options.background ?? idleFor() > BACKGROUND_AFTER_MS) headers['X-Background'] = '1'
  let body: BodyInit | undefined
  if (options.body instanceof FormData) {
    body = options.body
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(options.body)
  }
  if (method !== 'GET') headers['X-CSRFToken'] = csrfToken()

  let response: Response
  try {
    response = await fetch(url, { method, headers, body, credentials: 'same-origin', signal: options.signal })
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error
    throw new ApiError(0, 'offline', 'Connect to the internet and try again.')
  }
  const text = await response.text()
  const data = text ? safeJson(text) : null
  if (!response.ok) {
    const problem = (data as { error?: { code?: string; message?: string; details?: Record<string, unknown>;
      fields?: Record<string, string[]> } } | null)?.error
    const error = new ApiError(response.status, problem?.code ?? `http_${response.status}`,
      problem?.message ?? 'Something went wrong. Nothing was saved. Try again.', problem?.details ?? {},
      problem?.fields)
    if (error.sessionEnded) window.dispatchEvent(new CustomEvent(SESSION_ENDED_EVENT))
    else if (error.code === 'consent_required') window.dispatchEvent(new CustomEvent(CONSENT_REQUIRED_EVENT))
    throw error
  }
  return data as T
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], init?: Pick<RequestOptions, 'signal' | 'background'>) =>
    request<T>(path, { query, ...init }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
}

/** Paths under the signed-in group: group(id, 'home') → /groups/<id>/home */
export function groupPath(groupId: string, path: string): string {
  return `/groups/${groupId}/${path.replace(/^\//, '')}`
}
